import { useEffect, useId, useRef, type FocusEvent, type KeyboardEvent } from "react";
import { NavLink } from "react-router-dom";

import { Icon } from "../../ui/Icon";
import type { MoreGroup } from "../navigation";

/** The links of an open menu, in order. */
function linksOf(root: HTMLElement | null): HTMLAnchorElement[] {
  return root ? Array.from(root.querySelectorAll<HTMLAnchorElement>(".nav-menu__list a")) : [];
}

export interface NavMenuProps {
  /** The word on the button. It also names the list. */
  label: string;
  /** What the menu holds, said once for a screen reader. */
  summary?: string;
  groups: readonly MoreGroup[];
  /** The menu holds the screen the person is on. */
  current: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

/**
 * A menu of the bar: a button that opens a short list of links, in groups with a heading each. It is a disclosure, not a
 * pop-up menu: the links are reached with Tab like any other, and the arrow keys, Home, End and Escape are there as a help.
 * Escape closes the list and puts the focus back on the button.
 */
export function NavMenu({ label, summary, groups, current, open, onOpen, onClose }: NavMenuProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const focusFirst = useRef(false);

  // Opened with the keyboard (arrow down): the focus goes to the first link as soon as it is there.
  useEffect(() => {
    if (open && focusFirst.current) linksOf(rootRef.current)[0]?.focus();
    focusFirst.current = false;
  }, [open]);

  const onButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" && !open) {
      event.preventDefault();
      focusFirst.current = true;
      onOpen();
    } else if (event.key === "ArrowDown" && open) {
      event.preventDefault();
      linksOf(rootRef.current)[0]?.focus();
    }
  };

  const onRootKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      buttonRef.current?.focus();
      return;
    }
    const links = linksOf(rootRef.current);
    const index = links.findIndex((link) => link === document.activeElement);
    if (index < 0) return;
    const move = (to: number) => {
      event.preventDefault();
      links[(to + links.length) % links.length]?.focus();
    };
    if (event.key === "ArrowDown") move(index + 1);
    else if (event.key === "ArrowUp") {
      if (index === 0) {
        event.preventDefault();
        buttonRef.current?.focus();
      } else move(index - 1);
    } else if (event.key === "Home") move(0);
    else if (event.key === "End") move(links.length - 1);
  };

  // Tabbing away from the menu closes it. Losing the focus to nowhere (a click on the page) is the outside press's job.
  const onRootBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (open && next instanceof Element && !event.currentTarget.contains(next)) onClose();
  };

  // A press outside the menu closes it.
  useEffect(() => {
    if (!open) return;
    const onPress = (event: Event) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) onClose();
    };
    document.addEventListener("pointerdown", onPress);
    return () => document.removeEventListener("pointerdown", onPress);
  }, [open, onClose]);

  return (
    <div className="nav-menu" ref={rootRef} data-open={open || undefined} onKeyDown={onRootKeyDown} onBlur={onRootBlur}>
      <button
        type="button"
        ref={buttonRef}
        className="nav-menu__button"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-current={current ? "true" : undefined}
        data-current={current || undefined}
        title={summary}
        onClick={() => (open ? onClose() : onOpen())}
        onKeyDown={onButtonKeyDown}
      >
        {label}
        <Icon name="chevron-down" size={11} className="nav-menu__chevron" />
      </button>
      {open && (
        <ul id={listId} className="nav-menu__list" aria-label={label}>
          {groups.map((group) => (
            <li key={group.id} className="nav-menu__group">
              {group.label && (
                <span id={`${listId}-${group.id}`} className="nav-menu__heading">
                  {group.label}
                </span>
              )}
              <ul aria-labelledby={group.label ? `${listId}-${group.id}` : undefined} className="nav-menu__items">
                {group.items.map((item) => (
                  <li key={item.to}>
                    {/* Following a link to the screen the person is on changes no address, so the menu is closed here too. */}
                    <NavLink to={item.to} end={item.end} onClick={onClose}>
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
